<template>
  <div id="app">
    <div class="layout">
      <!-- Sidebar Navigation -->
      <aside class="sidebar" :class="{ open: sidebarOpen, collapsed: sidebarCollapsed }" ref="sidebar">
        <div class="sidebar-header">
<router-link to="/" class="brand-link" title="UniPhy" aria-label="UniPhy">UniPhy</router-link>
        </div

        <!-- Authenticated Navigation -->
        <div class="nav-links" v-if="store.getters['auth/isAuthenticated']">
          <router-link to="/dashboard" class="nav-link" title="Dashboard">
            <span class="icon">🏠</span><span class="label">Dashboard</span>
          </router-link>
          
          <!-- Organization (Super Admin Only) -->
          <router-link v-if="store.getters['auth/isSuperAdmin']" to="/organizations" class="nav-link" title="Organizations">
            <span class="icon">🏢</span><span class="label">Organizations</span>
          </router-link>
          
          <router-link to="/workstreams" class="nav-link" title="Workstream">
            <span class="icon">📊</span><span class="label">Workstream</span>
          </router-link>
          <router-link to="/treasury" class="nav-link" title="Treasury">
            <span class="icon">💰</span><span class="label">Treasury</span>
          </router-link>
          <router-link to="/governance" class="nav-link" title="Governance">
            <span class="icon">⚖️</span><span class="label">Governance</span>
          </router-link>
          <router-link to="/agents" class="nav-link" title="Agents">
            <span class="icon">🤖</span><span class="label">Agents</span>
          </router-link>
          <router-link to="/models" class="nav-link" title="Models">
            <span class="icon">🧠</span><span class="label">Models</span>
          </router-link>
          <router-link to="/knowledge-base" class="nav-link" title="Knowledge Base">
            <span class="icon">📚</span><span class="label">Knowledge Base</span>
          </router-link>
          <router-link to="/users" class="nav-link" title="Users">
            <span class="icon">👥</span><span class="label">Users</span>
          </router-link>
          <router-link to="/roles" class="nav-link" title="User Permissions">
            <span class="icon">🔐</span><span class="label">User Permissions</span>
          </router-link>
          <router-link to="/integration" class="nav-link" title="Integration">
            <span class="icon">🔗</span><span class="label">Integration</span>
          </router-link>
          <router-link to="/api-docs" class="nav-link" title="API Docs">
            <span class="icon">📋</span><span class="label">API Docs</span>
          </router-link>
        </div>

        <!-- Unauthenticated -->
        <div class="nav-links" v-else>
          <router-link to="/login" class="nav-link login-link">
            <span class="icon">🔐</span><span class="label">Login</span>
          </router-link>
        </div>

        <div class="sidebar-spacer"></div>

        <!-- Collapse toggle (desktop) -->
        <button 
          class="collapse-toggle" 
          @click="toggleSidebarCollapse" 
          :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        >
          {{ sidebarCollapsed ? '»' : '«' }}
        </button

        <!-- User Section at bottom -->
        <div class="user-section" v-if="store.getters['auth/isAuthenticated']">
          <div class="user-menu" @click="toggleUserMenu" ref="userMenu">
            <div class="user-avatar">
              <span>{{ userInitials }}</span>
            </div>
            <div class="user-info">
              <span class="user-name">{{ store.state.auth.user?.email }}</span>
              <span class="org-name" v-if="store.state.auth.orgId">{{ store.state.auth.orgId }}</span>
            </div>
            <div class="dropdown-menu" v-show="showUserMenu">
              <router-link to="/profile" class="menu-item">
                <i class="icon-user"></i> Profile
              </router-link>
              <router-link to="/integration" class="menu-item">
                <i class="icon-settings"></i> Integration
              </router-link>
              <a href="#" @click.prevent="toggleCompactDensity" class="menu-item">
                <i class="icon-compact"></i> {{ compactDensity ? 'Compact mode: On' : 'Compact mode: Off' }}
              </a>
              <div class="menu-divider"></div>
              <a href="#" @click.prevent="handleLogout" class="menu-item text-danger">
                <i class="icon-logout"></i> Logout
              </a>
            </div>
          </div>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="app-content">
        <button class="sidebar-toggle" @click="sidebarOpen = !sidebarOpen" aria-label="Toggle navigation">
          ☰
        </button>
        <router-view/>
      </main>
    </div>
  </div>
</template>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --sidebar-width: 260px;
  --sidebar-collapsed-width: 72px;
  --sidebar-bg: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --sidebar-text: rgba(255,255,255,0.9);
}

#app {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  height: 100vh;
}

.layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

/* Sidebar */
.sidebar {
  position: sticky;
  top: 0;
  left: 0;
  height: 100vh;
  width: var(--sidebar-width);
  background: var(--sidebar-bg);
  color: white;
  display: flex;
  flex-direction: column;
  padding: 1rem 0.75rem;
  box-shadow: 2px 0 10px rgba(0,0,0,0.08);
  z-index: 1000;
  transform: translateX(0);
  transition: transform 0.2s ease-in-out, width 0.2s ease-in-out;
}

.sidebar.collapsed {
  width: var(--sidebar-collapsed-width);
}

.sidebar.collapsed .label,
.sidebar.collapsed .user-info {
  display: none;
}

.sidebar.collapsed .nav-link {
  justify-content: center;
  padding: 0.6rem;
}

.sidebar.collapsed .nav-link .icon {
  width: 24px;
}

.sidebar-header {
  padding: 0 0.5rem 1rem 0.5rem;
}

.brand-link {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-decoration: none;
  letter-spacing: 0.2px;
  display: block;
  text-align: left;
}

.sidebar.collapsed .brand-link {
  text-align: center;
  font-size: 1.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
}

.nav-links {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--sidebar-text);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  transition: background 0.2s, color 0.2s;
}

.nav-link:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
}

.nav-link.router-link-active {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.22);
  font-weight: 600;
}

.nav-link .icon {
  width: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.login-link {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

/* Spacer pushes user section to bottom */
.sidebar-spacer { flex: 1; }

/* User Section */
.user-section { position: relative; }

.user-menu {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
}

.user-menu:hover { background: rgba(255, 255, 255, 0.2); }

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: white;
}

.user-info { display: flex; flex-direction: column; }
.user-name { font-size: 0.9rem; font-weight: 500; color: white; }
.org-name { font-size: 0.75rem; color: rgba(255, 255, 255, 0.7); }

.dropdown-menu {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  right: 0;
  background: white;
  color: #2c3e50;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  padding: 0.5rem;
  z-index: 1001;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  color: #2c3e50;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.2s ease;
}
.menu-item:hover { background: #f8f9fa; }
.menu-divider { height: 1px; background: #e9ecef; margin: 0.5rem 0; }
.text-danger { color: #dc3545; }
.text-danger:hover { background: #fff5f5; }

/* Main Content */
.app-content {
  flex: 1;
  overflow-y: auto;
  background: #f8f9fa;
  padding: 0;
  position: relative;
}

/* On desktop, reserve space for sidebar */
@media (min-width: 768px) {
  .app-content { margin-left: var(--sidebar-width); }
  body.sidebar-collapsed .app-content { margin-left: var(--sidebar-collapsed-width); }
  .sidebar { position: fixed; }
}

/* Sidebar toggle */
.sidebar-toggle {
  display: none; /* hidden by default (desktop) */
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 1100;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.4rem 0.6rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  cursor: pointer;
}

/* Mobile behavior: sidebar overlays */
@media (max-width: 767px) {
  .sidebar {
    position: fixed;
    transform: translateX(-100%);
  }
  .sidebar.open { transform: translateX(0); }
  .app-content { margin-left: 0; }
  .sidebar-toggle { display: inline-flex; }
}

/* Global Styles */
body {
  margin: 0;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f8f9fa;
}

/* Density: compact mode overrides */
body.density-compact .page-header { padding: 0.5rem 0.75rem; }
body.density-compact .page-header h1 { font-size: 1.25rem; }
body.density-compact .panel-header { padding: 0.75rem 1rem; }
body.density-compact .left-panel,
body.density-compact .org-left-panel { width: 280px; }
body.density-compact .agent-item,
body.density-compact .kb-item,
body.density-compact .page-list-item,
body.density-compact .provider-item,
body.density-compact .category-item,
body.density-compact .org-item { padding: 0.5rem 0.75rem; }
body.density-compact .kb-info p,
body.density-compact .page-info p,
body.density-compact .provider-info p,
body.density-compact .user-email,
body.density-compact .role-description { font-size: 0.85rem; }
/* Dashboard compact tweaks */
body.density-compact .dashboard-container .metrics-grid { gap: 0.75rem; }
body.density-compact .dashboard-container .metric-card { padding: 1rem; }
body.density-compact .dashboard-container .secondary-section { gap: 1rem; }
body.density-compact .dashboard-container .quick-actions-grid { gap: 0.75rem; }

/* Icon styles (unchanged) */
.icon-user:before { content: '👤'; }
.icon-settings:before { content: '⚙️'; }
.icon-logout:before { content: '🚪'; }
.icon-admin:before { content: '🔐'; }
.icon-org:before { content: '🏢'; }
.icon-users:before { content: '👥'; }
.icon-compact:before { content: '📏'; }

/* Scrollbar Styling */
::-webkit-scrollbar { width: 8px; }
::-webkit-scrollbar-track { background: #f1f1f1; }
::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }

/* Sidebar collapse toggle button */
.collapse-toggle {
  display: none; /* show on desktop only */
  margin: 0.5rem auto;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(255,255,255,0.12);
  color: #fff;
  border-radius: 8px;
  cursor: pointer;
}

@media (min-width: 768px) {
  .collapse-toggle { display: inline-flex; align-items: center; justify-content: center; }
}
</style>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

export default {
  name: 'App',
  setup() {
    const store = useStore()
    const router = useRouter()
    const showUserMenu = ref(false)
    const userMenu = ref(null)
    const sidebar = ref(null)
    const sidebarOpen = ref(typeof window !== 'undefined' ? window.innerWidth >= 768 : true)
    const sidebarCollapsed = ref(false)
    const compactDensity = ref(false)

    const userInitials = computed(() => {
      const email = store.state.auth.user?.email || ''
      return email
        .split('@')[0]
        .split(/[^a-zA-Z]/)
        .filter(part => part)
        .map(part => part[0].toUpperCase())
        .slice(0, 2)
        .join('')
    })

    const isAdminOrAbove = computed(() => store.getters['auth/isAdminOrAbove'])

    const handleLogout = () => {
      store.dispatch('auth/logout')
      showUserMenu.value = false
      router.push('/login')
    }

    const toggleUserMenu = () => { showUserMenu.value = !showUserMenu.value }

    const applySidebarClasses = () => {
      if (typeof document !== 'undefined') {
        if (sidebarCollapsed.value) document.body.classList.add('sidebar-collapsed')
        else document.body.classList.remove('sidebar-collapsed')
      }
    }

    const toggleSidebarCollapse = () => {
      if (typeof window !== 'undefined' && window.innerWidth < 768) return
      sidebarCollapsed.value = !sidebarCollapsed.value
      try { localStorage.setItem('sidebarCollapsed', sidebarCollapsed.value ? 'true' : 'false') } catch (e) {}
      applySidebarClasses()
    }

    const applyDensityClass = () => {
      if (typeof document !== 'undefined') {
        if (compactDensity.value) document.body.classList.add('density-compact')
        else document.body.classList.remove('density-compact')
      }
    }

    const toggleCompactDensity = () => {
      compactDensity.value = !compactDensity.value
      try { localStorage.setItem('densityCompact', compactDensity.value ? 'true' : 'false') } catch (e) {}
      applyDensityClass()
    }

    // Close menus and sidebar (mobile) when clicking outside
    const handleClickOutside = (event) => {
      // Close user menu
      if (userMenu.value && !userMenu.value.contains(event.target)) {
        showUserMenu.value = false
      }
      // Close sidebar on mobile when clicking outside
      if (typeof window !== 'undefined' && window.innerWidth < 768 && sidebar.value && !sidebar.value.contains(event.target)) {
        sidebarOpen.value = false
      }
    }

    const handleResize = () => {
      if (typeof window !== 'undefined' && window.innerWidth >= 768) {
        sidebarOpen.value = true
      }
    }

    onMounted(async () => {
      document.addEventListener('click', handleClickOutside)
      window.addEventListener('resize', handleResize)

      // Restore sidebar collapsed state
      try {
        const savedCollapsed = localStorage.getItem('sidebarCollapsed')
        sidebarCollapsed.value = savedCollapsed === 'true'
      } catch (e) {}
      applySidebarClasses()

      // Restore compact density state
      try {
        const savedDensity = localStorage.getItem('densityCompact')
        compactDensity.value = savedDensity === 'true'
      } catch (e) {}
      applyDensityClass()

      await store.dispatch('auth/initializeAuth')
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('resize', handleResize)
    })

    return {
      store,
      showUserMenu,
      userMenu,
      sidebar,
      sidebarOpen,
      userInitials,
      isAdminOrAbove,
      isSuperAdmin: computed(() => store.getters['auth/isSuperAdmin']),
      handleLogout,
      toggleUserMenu,
      sidebarCollapsed,
      toggleSidebarCollapse,
      compactDensity,
      toggleCompactDensity
    }
  }
}
</script>
